import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { categoryService } from '@/services'

export const useCategoriesStore = defineStore('categories', () => {
  // State
  const tree = ref([])
  const flat = ref([])
  const loading = ref(false)
  const error = ref(null)
  let lastFetchedAt = 0
  let inflight = null

  // Getters
  const getTree = computed(() => tree.value)
  const getFlat = computed(() => flat.value)
  const isLoading = computed(() => loading.value)
  const getError = computed(() => error.value)

  // Actions
  const fetchTree = async ({ force = false } = {}) => {
    const now = Date.now()
    if (!force && tree.value.length > 0 && (now - lastFetchedAt < 5 * 60 * 1000)) return tree.value
    if (!force && inflight) return inflight
    loading.value = true
    error.value = null
    try {
      inflight = categoryService.getTree({ nested: true })
      const data = await inflight
      tree.value = Array.isArray(data) ? data : []
      flat.value = flattenTree(tree.value)
      lastFetchedAt = Date.now()
      return tree.value
    } catch (err) {
      error.value = err?.message || 'Failed to load categories'
      throw err
    } finally {
      loading.value = false
      inflight = null
    }
  }

  const createCategory = async (payload) => {
    const res = await categoryService.create(payload)
    await fetchTree({ force: true })
    return res
  }

  const updateCategory = async (id, payload) => {
    const res = await categoryService.update(id, payload)
    await fetchTree({ force: true })
    return res
  }

  const moveCategory = async (id, payload) => {
    const res = await categoryService.moveCategory(id, payload)
    await fetchTree({ force: true })
    return res
  }

  const reorderChildren = async (id, orders) => {
    const res = await categoryService.reorderChildren(id, orders)
    await fetchTree({ force: true })
    return res
  }

  const deleteCategory = async (id) => {
    const res = await categoryService.delete(id)
    await fetchTree({ force: true })
    return res
  }

  const flattenTree = (nodes) => {
    const out = []
    const walk = (arr, parentBreadcrumb = []) => {
      for (const n of arr) {
        const breadcrumb = [...parentBreadcrumb, n.name]
        out.push({
          id: n.id,
          name: n.name,
          slug: n.slug,
          path: n.path,
          depth: n.depth,
          sort_order: n.sort_order,
          is_active: n.is_active,
          include_all_products_in_grid: !!n.include_all_products_in_grid,
          convenience_fee_mode: n.convenience_fee_mode ?? null,
          deletable: n.deletable,
          delete_reason: n.delete_reason,
          color: n.color,
          icon: n.icon,
          image_url: n.image_url,
          breadcrumb,
          breadcrumbLabel: breadcrumb.join(' / ')
        })
        if (n.children && n.children.length) walk(n.children, breadcrumb)
      }
    }
    walk(nodes)
    return out
  }

  const clearError = () => { error.value = null }

  return {
    tree,
    flat,
    loading,
    error,
    getTree,
    getFlat,
    isLoading,
    getError,
    fetchTree,
    createCategory,
    updateCategory,
    moveCategory,
    reorderChildren,
    deleteCategory,
    clearError
  }
})


